import { Injectable, Injector } from "@angular/core";
import { TrackingDispatchService } from "./tracking-dispatch.service";
import { NavigationEventRequest, NavigationTriggerType } from "./tracking.models";
import { routePath } from "./route-path.util";

// Re-exported so the ~24 call sites that import NavigationTriggerType from here keep working. The
// definitions moved to tracking.models.ts to break an import cycle with TrackingDispatchService.
export { NavigationEventRequest, NavigationTriggerType };

/**
 * Records the app's two navigation surfaces: the persistent bottom nav bar (ViaNavigation) and
 * in-page links/cards/buttons that change screen (InPageNav). Dialogs/modals don't call this - the
 * route doesn't change, so they're not navigation. See "Full App Event-Tracking Taxonomy"
 * (Confluence FLIP-110919682) Section 1.
 *
 * Since FLIP-213 this only queues onto TrackingDispatchService, which batches everything fired in
 * the same tick into one POST to api/Tracking/Track. The public signature is unchanged, so no call
 * site needed touching.
 *
 * FLIP-212: it also remembers where each explicit call said the user was going, so the router-level
 * tracker (RouterNavigationTrackerService) can tell a route change that was already recorded from
 * one nobody instrumented, and not write the same navigation twice.
 */
@Injectable({
  providedIn: "root",
})
export class NavigationTrackingService {
  // Resolved lazily via Injector so a component injecting only this service doesn't drag
  // UserService (a TrackingDispatchService dependency) into its construction order.
  constructor(private injector: Injector) {}

  /** Explicit calls not yet matched to a route change. Short-lived: pruned on every write. */
  private explicit: { path: string; at: number; consumed: boolean }[] = [];

  /** Fire-and-forget: tracking must never block or break navigation if it fails. */
  track(event: NavigationEventRequest): void {
    this.remember(event.toScreen);
    this.injector.get(TrackingDispatchService).queueNavigation(event);
  }

  /**
   * FLIP-212: true, and marks the match used, if an explicit call recorded at or after `since`
   * named any of `paths` as its destination. Each explicit call covers at most ONE route change, so
   * a second visit to the same screen is not swallowed by the first visit's event.
   */
  consumeExplicit(paths: string[], since: number): boolean {
    const wanted = paths.map(routePath).filter((p) => p.length > 0);
    const match = this.explicit.find((e) => !e.consumed && e.at >= since && wanted.includes(e.path));
    if (!match) {
      return false;
    }
    match.consumed = true;
    return true;
  }

  private remember(toScreen: string): void {
    const now = Date.now();
    // 60s is far past any lookback the router tracker uses; it only bounds the list.
    this.explicit = this.explicit.filter((e) => now - e.at < 60000);
    this.explicit.push({ path: routePath(toScreen), at: now, consumed: false });
  }
}
